import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate, monthName } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import {
  ART_KURZ,
  ART_NAME,
  auftraegeUrl,
  auftragInfo,
  auftragMenue,
  auftragPdfUrl,
  auftragSchritt,
  auftragUrl,
  neuerAuftrag,
  neuerLieferschein,
  rechnungAusLieferscheinen,
  statusOf,
  vorgangText,
  type AuftragEintrag,
  type AuftragItem,
  type AuftragKontext,
  type AuftragListe,
} from './AuftragDaten';
import { AuftragForm } from './AuftragForm';
import { LieferscheinForm } from './LieferscheinForm';
import { PdfDialog } from './PdfDialog';
import { darf, MitRecht, NurAnsehen } from './tarifHinweis';
import type { Overview } from './types';
import { VersandDialog, versandText, type VersandBeleg } from './VersandDialog';
import { ZeilenMenue } from './ZeilenMenue';
import './auftraege.css';

// Aufträge (/rechnung/auftraege): Auftragsbestätigungen und Lieferscheine in einer Liste mit Kennzahlen, Filter, Suche
// und Mehrfachwahl (Rechnung aus mehreren Lieferscheinen); /rechnung/auftraege/neu: neue Auftragsbestätigung
// (AuftragForm.tsx), /rechnung/auftraege/lieferschein: neuer Lieferschein (LieferscheinForm.tsx). Keine Entwürfe.
export function Auftraege({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  // Billtoro: Formulare nur mit dem Recht „Angebote und Aufträge“, sonst zurück zur Liste mit Hinweis
  const recht = (form: ReactNode) => (
    <MitRecht tarif={data.tarif} bereich="angebote" zu="/rechnung/auftraege">
      {form}
    </MitRecht>
  );
  return (
    <Routes>
      <Route index element={<Liste data={data} />} />
      <Route path="neu" element={recht(<AuftragForm data={data} reload={reload} />)} />
      <Route path="lieferschein" element={recht(<LieferscheinForm data={data} reload={reload} />)} />
      <Route path="*" element={<Navigate to="/rechnung/auftraege" replace />} />
    </Routes>
  );
}

type View = { url: string; title: string; beleg?: VersandBeleg; hinweis?: string | null };
const titelOf = (x: Pick<AuftragItem, 'art' | 'number'>) => `${ART_NAME[x.art]} ${x.number}`;
const beleg = (x: AuftragItem): VersandBeleg => ({ art: x.art, id: x.id, titel: titelOf(x) });
// „Per E-Mail“ im Fenster der PDF nicht bei stornierten Belegen; wann und an wen verschickt steht dort immer (auch ohne
// das Recht und bei stornierten, wo das Fenster „Per E-Mail“ fehlt)
const viewOf = (x: AuftragItem): View => ({
  url: auftragPdfUrl(x.art, x.id),
  title: titelOf(x),
  beleg: x.status === 'storniert' ? undefined : beleg(x),
  hinweis: versandText(x.versand),
});

type Filter = 'alle' | 'auftrag' | 'lieferschein' | 'offen' | 'berechnet';
const FILTERS: [Filter, string][] = [
  ['alle', 'Alle'],
  ['auftrag', 'Auftragsbestätigungen'],
  ['lieferschein', 'Lieferscheine'],
  ['offen', 'Noch zu berechnen'],
  ['berechnet', 'Berechnet'],
];
const filterOf = (v: string | null): Filter | null => FILTERS.find(([f]) => f === v)?.[0] ?? null;
// gewählter Filter auf diesem Gerät (?filter= in der Adresse geht vor, z. B. „Noch zu berechnen“ aus der Übersicht)
const MERK = 'rt-auftraege-filter';
function gemerkterFilter(): Filter {
  try {
    return filterOf(localStorage.getItem(MERK)) ?? 'alle';
  } catch {
    return 'alle';
  }
}
function merken(f: Filter) {
  try {
    localStorage.setItem(MERK, f);
  } catch {
    // ohne Speicher: beim nächsten Mal wieder „Alle“
  }
}
const matches = (x: AuftragItem, f: Filter) =>
  f === 'alle' ? true : f === 'offen' ? x.berechenbar : f === 'berechnet' ? x.anzeige === 'berechnet' || x.anzeige === 'zur_rechnung' : x.art === f;
// Suche: Nummer (auch Nummern im Vorgang, z. B. das Angebot), Kunde, Einsatz- bzw. Lieferort, Bestellnummer
function found(x: AuftragItem, q: string) {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [x.number, x.customer, x.site ?? '', x.bestellnummer ?? '', vorgangText(x.vorgang)].some((s) => s.toLowerCase().includes(t));
}
const belege = (n: number) => (n === 1 ? '1 Beleg' : `${n} Belege`);
// derselbe Kunde wie auf dem Server (auftragstand.ts): beide in der Kundenliste dieselbe Kundin, sonst Name und PLZ
const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
const gleicherKunde = (a: AuftragItem, b: AuftragItem) =>
  a.kundeId && b.kundeId ? a.kundeId === b.kundeId : norm(a.customer) === norm(b.customer) && a.zip.replace(/\s/g, '') === b.zip.replace(/\s/g, '');
// höchstens so viele Lieferscheine in eine Rechnung (wie der Server)
const MAX_LS = 20;

function Liste({ data }: { data: Overview }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [list, setList] = useState<AuftragListe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [send, setSend] = useState<VersandBeleg | null>(null);
  const [storno, setStorno] = useState<AuftragItem | null>(null);
  const [busy, setBusy] = useState(false);
  // Billtoro: Rechte des Logins (nur Anzeige, der Server prüft); Rechnung aus Auftrag oder Lieferschein braucht „Rechnungen“
  const bieten = darf(data.tarif, 'angebote');
  const rechnen = darf(data.tarif, 'rechnungen');
  const ctx: AuftragKontext = { bieten, rechnen };
  // gewählte Lieferscheine für eine Rechnung
  const [sel, setSel] = useState<string[]>([]);
  const [params] = useSearchParams();
  const urlFilter = filterOf(params.get('filter'));
  const urlSuche = params.get('suche');
  const [filter, setFilterState] = useState<Filter>(() => urlFilter ?? (urlSuche !== null ? 'alle' : gemerkterFilter()));
  const [q, setQ] = useState(urlSuche ?? '');
  const setFilter = (f: Filter) => {
    setFilterState(f);
    merken(f);
  };
  useEffect(() => {
    if (urlFilter) setFilterState(urlFilter);
  }, [urlFilter]);
  // Link aus dem Vorgang oder „Zum Auftrag“ beim Angebot: nach der Nummer suchen, alle Arten zeigen
  useEffect(() => {
    if (urlSuche === null) return;
    setQ(urlSuche);
    setFilterState('alle');
  }, [urlSuche]);

  const load = useCallback(async () => {
    try {
      const l = await api.get<AuftragListe>(auftraegeUrl(data.firm.id));
      setList(l);
      // nur, was noch zu berechnen ist, bleibt gewählt
      setSel((s) => s.filter((id) => l.items.some((x) => x.id === id && x.art === 'lieferschein' && x.berechenbar)));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [data.firm.id]);
  useEffect(() => {
    void load();
  }, [load]);

  // Gerade geschrieben: gleich die PDF zeigen, zum Herunterladen und Verschicken
  useEffect(() => {
    const st = location.state as { show?: string; art?: AuftragItem['art']; number?: string } | null;
    if (st?.show && st.art) {
      const title = titelOf({ art: st.art, number: st.number ?? '' });
      setView({ url: auftragPdfUrl(st.art, st.show), title, beleg: { art: st.art, id: st.show, titel: title } });
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  async function stornieren(x: AuftragItem) {
    setBusy(true);
    try {
      await api.post(`${auftragUrl(x.art, x.id)}/storno`, {});
      toast(`${titelOf(x)} storniert`);
      setStorno(null);
      await load();
    } catch (err) {
      setStorno(null);
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // Eintrag im Menü „···“ der Zeile
  function eintrag(e: AuftragEintrag, x: AuftragItem, n: number) {
    switch (e.art) {
      case 'trenner':
        return <div key={`trenner-${n}`} role="separator" />;
      case 'mail':
        return (
          <button key={e.text} type="button" role="menuitem" onClick={() => setSend(beleg(x))}>
            {e.text}
          </button>
        );
      case 'storno':
        return (
          <button key={e.text} type="button" role="menuitem" className="rt-danger" onClick={() => setStorno(x)}>
            {e.text}
          </button>
        );
      case 'link':
        return (
          <Link key={e.text} role="menuitem" to={e.ziel}>
            {e.text}
          </Link>
        );
    }
  }

  // Kopf: der weiße Knopf vor dem orangen (am Handy der orange zuerst, beide in einer Reihe, auftraege.css)
  const head = (
    <div className="rt-head au-head">
      <div>
        <h1>
          Aufträge <NurAnsehen tarif={data.tarif} bereich="angebote" />
        </h1>
        <p className="muted">{list === null ? '' : list.items.length ? belege(list.items.length) : 'Noch keine Auftragsbestätigungen und Lieferscheine'}</p>
      </div>
      {bieten && (
        <div className="rt-head-act">
          <Link to={neuerLieferschein()} className="btn">
            + Neuer Lieferschein
          </Link>
          <Link to={neuerAuftrag()} className="btn primary">
            + Neue Auftragsbestätigung
          </Link>
        </div>
      )}
    </div>
  );
  if (list === null) {
    return (
      <>
        {head}
        {error ? <div className="notice bad">{error}</div> : <p className="muted">Lädt …</p>}
      </>
    );
  }

  const k = list.kennzahlen;
  const shown = list.items.filter((x) => matches(x, filter) && found(x, q));
  const kpi = (f: Filter) => () => setFilter(filter === f ? 'alle' : f);
  const gewaehlt = sel.map((id) => list.items.find((x) => x.id === id)).filter((x): x is AuftragItem => !!x);
  const einKunde = gewaehlt.every((x) => gleicherKunde(x, gewaehlt[0]!));
  // eine Rechnung hat eine Währung: nur Lieferscheine in derselben
  const eineWaehrung = gewaehlt.every((x) => x.waehrung === gewaehlt[0]!.waehrung);
  const toggle = (id: string, an: boolean) => setSel((s) => (an ? [...s.filter((x) => x !== id), id] : s.filter((x) => x !== id)));

  return (
    <>
      {head}
      {list.items.length > 0 && (
        <>
          <div className="rt-kpis re-kpis au-kpis">
            <button type="button" className={`rt-kpi${filter === 'auftrag' ? ' on' : ''}`} onClick={kpi('auftrag')}>
              <span className="rt-kpi-l">Offene Aufträge</span>
              <span className="rt-kpi-v num">{k.offen}</span>
              <span className="rt-kpi-s">noch zu liefern</span>
            </button>
            {/* in Tinte: grün nur für Geld, das eingegangen ist */}
            <button type="button" className={`rt-kpi${filter === 'offen' ? ' on' : ''}`} onClick={kpi('offen')}>
              <span className="rt-kpi-l">Noch zu berechnen</span>
              <span className="rt-kpi-v num">{eurC(k.zuBerechnen.betrag)}</span>
              <span className="rt-kpi-s">
                {k.zuBerechnen.anzahl ? belege(k.zuBerechnen.anzahl) : 'alles berechnet'}
                {list.items.some((x) => x.berechenbar && x.waehrung !== 'EUR') ? ' · umgerechnet zum Kurs der Belege' : ''}
              </span>
            </button>
            <button type="button" className={`rt-kpi${filter === 'lieferschein' ? ' on' : ''}`} onClick={kpi('lieferschein')}>
              <span className="rt-kpi-l">Lieferungen im Monat</span>
              <span className="rt-kpi-v num">{k.lieferungenMonat}</span>
              <span className="rt-kpi-s">{monthName(Number(list.heute.slice(5, 7)))}</span>
            </button>
          </div>
          <div className="rt-tools">
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Nummer, Kunde oder Ort"
              title="Suchen: Nummer, Kunde, Ort oder Bestellnummer"
              aria-label="Aufträge suchen"
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="rt-chips" role="group" aria-label="Filter">
              {FILTERS.map(([f, label]) => (
                <button key={f} type="button" className={`rt-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {list.items.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Bestätigen Sie einen Auftrag oder schreiben Sie einen Lieferschein. Aus beiden wird mit einem Klick die Rechnung.</p>
          {bieten && (
            <Link to={neuerAuftrag()} className="btn primary">
              Erste Auftragsbestätigung schreiben
            </Link>
          )}
        </div>
      ) : shown.length === 0 ? (
        <div className="rt-card rt-empty">
          <p>Nichts passt zu dieser Auswahl.</p>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setFilter('alle');
              setQ('');
            }}
          >
            Alle zeigen
          </button>
        </div>
      ) : (
        <ul className="rt-list re-list au-list">
          {shown.map((x) => {
            const st = statusOf(x);
            const an = sel.includes(x.id);
            // Rechnung aus mehreren Lieferscheinen: Haken vor dem Knopf
            const waehlbar = x.art === 'lieferschein' && x.berechenbar && rechnen;
            const schritt = auftragSchritt(x, ctx);
            const eintraege = auftragMenue(x, ctx, schritt);
            const info = auftragInfo(x, list.heute);
            return (
              <li key={x.id} className={`rt-row${an ? ' au-an' : ''}`}>
                <button type="button" className="rt-row-main" onClick={() => setView(viewOf(x))}>
                  <span className="rt-no re-no2">
                    <span className="num">{x.number}</span> <span className="re-art">{ART_KURZ[x.art]}</span>
                  </span>
                  <span className="re-kunde">
                    <span className="rt-cust" title={x.customer}>
                      {x.customer}
                    </span>
                    {info && (
                      <span className="rt-row-info">
                        <bdi>{info}</bdi>
                      </span>
                    )}
                  </span>
                  <span className="rt-date muted">{fmtDate(x.date)}</span>
                  {x.art === 'lieferschein' && !x.mitPreisen ? (
                    <span className="rt-amt au-ohne muted">ohne Preise</span>
                  ) : (
                    <span className="rt-amt num">{eurC(x.betrag, x.waehrung)}</span>
                  )}
                  <span className={`badge ${st.cls}`}>{st.text}</span>
                </button>
                {(waehlbar || schritt || eintraege.length > 0) && (
                  <div className="re-act">
                    {waehlbar && (
                      <label className="check re-wahl" title="Für eine Rechnung aus mehreren Lieferscheinen wählen">
                        <input
                          type="checkbox"
                          checked={an}
                          disabled={!an && sel.length >= MAX_LS}
                          aria-label={`Lieferschein ${x.number} für die Rechnung wählen`}
                          onChange={(e) => toggle(x.id, e.target.checked)}
                        />
                      </label>
                    )}
                    {schritt && (
                      <div className="rt-next">
                        <Link to={schritt.ziel} className="btn small">
                          {schritt.text}
                        </Link>
                      </div>
                    )}
                    {eintraege.length > 0 && <ZeilenMenue nummer={x.number}>{eintraege.map((e, n) => eintrag(e, x, n))}</ZeilenMenue>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {gewaehlt.length > 0 && (
        <div className="au-leiste" role="region" aria-label="Gewählte Lieferscheine">
          <div className="au-leiste-t">
            <b>{gewaehlt.length === 1 ? '1 Lieferschein gewählt' : `${gewaehlt.length} Lieferscheine gewählt`}</b>
            {!einKunde && <small className="err">Nur Lieferscheine an denselben Kunden</small>}
            {einKunde && !eineWaehrung && <small className="err">Nur Lieferscheine in derselben Währung</small>}
          </div>
          <div className="au-leiste-act">
            <button type="button" className="btn small" onClick={() => setSel([])}>
              Auswahl aufheben
            </button>
            <button
              type="button"
              className="btn primary small"
              disabled={!einKunde || !eineWaehrung}
              onClick={() => navigate(rechnungAusLieferscheinen(gewaehlt.map((x) => x.id)))}
            >
              {gewaehlt.length === 1 ? 'Rechnung aus 1 Lieferschein schreiben' : `Rechnung aus ${gewaehlt.length} Lieferscheinen schreiben`}
            </button>
          </div>
        </div>
      )}

      <PdfDialog
        url={view?.url ?? null}
        title={view?.title ?? ''}
        download={view ? `${view.url}?download=1` : undefined}
        onClose={() => setView(null)}
        hinweis={view?.hinweis}
        onMail={
          view?.beleg && bieten
            ? () => {
                setSend(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
      />
      <VersandDialog beleg={send} tarif={data.tarif} onClose={() => setSend(null)} onSent={load} />
      <ConfirmDialog
        open={!!storno}
        title={storno ? `${titelOf(storno)} stornieren?` : ''}
        text={`${storno?.art === 'lieferschein' ? 'Er' : 'Sie'} bleibt gespeichert und wird nicht mehr berechnet.`}
        confirmLabel="Stornieren"
        danger
        busy={busy}
        onConfirm={() => storno && void stornieren(storno)}
        onCancel={() => setStorno(null)}
      />
    </>
  );
}
